/**
 * Modal "Marcar venda pela vendedora" no detalhe do lead.
 *
 * E o MESMO dialogo da fila do Comercial (`ModalVendaAssistida`), chamando a
 * mesma `registrarVendaAssistida` (POST /api/leads/:id/venda-assistida): valor
 * e referencia validados antes da rede, botao travado durante o envio, erros
 * 400/403/404 em frase clara e o desfecho em linguagem de negocio. Aqui so
 * se traduz o resultado para o que a pagina do lead guarda (`VendaMarcada`).
 *
 * ADR-001 E5: quem fechou e informacao ("fechado por"); a comissao, quando
 * existe, e do ultimo toque do motor -- o que a linha do tempo deste lead
 * mostra como contato.
 */

import { useMemo } from 'react';
import type { RefundPolicy, TeamMember } from '../../../types';
import { ModalVendaAssistida, type VendaAssistidaRegistrada } from '../../comercial/components/ModalVendaAssistida';
import type { ToqueDoMotor } from '../../comercial/types';
import { rotuloDoCredito } from '../../comercial/vendaAssistida';
import type { DesfechoDaVendaLocal } from '../resumo';

export { parseReaisParaCentavos } from '../../comercial/vendaAssistida';

export interface VendaMarcada {
  readonly sellerName: string;
  readonly amountCents: number;
  /** 0 quando a venda nao gerou comissao. */
  readonly commissionCents: number;
  readonly feeBpsApplied: number;
  readonly orderRef: string;
  readonly paidAt: string;
  readonly desfecho: DesfechoDaVendaLocal;
  /** Toque que recebeu o credito ("WhatsApp T+1h"); `null` sem credito. */
  readonly creditadoA: string | null;
  /** Selo curto do desfecho ("Comissão creditada", "Sem comissão"). */
  readonly selo: string;
}

/** Comercial primeiro (quem mais fecha), depois o resto da equipe, na ordem do cadastro. */
function ordenarEquipe(equipe: readonly TeamMember[]): readonly { readonly id: string; readonly name: string }[] {
  const comercial = equipe.filter((m) => m.role === 'comercial');
  const resto = equipe.filter((m) => m.role !== 'comercial' && m.role !== 'platform_admin');
  return [...comercial, ...resto].map((m) => ({ id: m.id, name: m.name }));
}

export function ModalMarcarVenda({
  open,
  onClose,
  onConfirm,
  equipe,
  valorSugeridoCents,
  feeBps,
  leadId,
  companyId,
  toque,
  agora,
  politicaDeEstorno,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Chamado com o desfecho do servidor (so gravacao nova); o dialogo continua aberto mostrando o resultado. */
  readonly onConfirm: (venda: VendaMarcada) => void;
  readonly equipe: readonly TeamMember[];
  readonly valorSugeridoCents: number;
  readonly feeBps: number;
  readonly leadId: string;
  readonly companyId: string;
  /** Ultimo toque do motor na linha do tempo deste lead; `null` = nenhum. */
  readonly toque: ToqueDoMotor | null;
  readonly agora: Date;
  /** `companies.refund_policy` da empresa em foco; ausente = padrao do schema. */
  readonly politicaDeEstorno?: RefundPolicy;
}): JSX.Element | null {
  const vendedoras = useMemo(() => ordenarEquipe(equipe), [equipe]);

  const traduzir = (registro: VendaAssistidaRegistrada): void => {
    const conversao = registro.resposta.conversion;
    if (conversao === 'duplicado') {
      return;
    }
    onConfirm({
      sellerName: registro.sellerName ?? 'Equipe comercial',
      amountCents: registro.amountCents,
      commissionCents: conversao === 'creditado' ? registro.resposta.commissionCents ?? 0 : 0,
      feeBpsApplied: feeBps,
      orderRef: registro.orderRef,
      paidAt: registro.paidAt,
      desfecho: conversao,
      creditadoA: conversao === 'sem_contato' ? null : rotuloDoCredito(registro.resposta, registro.toque),
      selo: registro.desfecho.selo,
    });
  };

  return (
    <ModalVendaAssistida
      open={open}
      onClose={onClose}
      leadId={leadId}
      companyId={companyId}
      titulo="Marcar venda pela vendedora"
      descricao="Venda fechada fora da régua automática (Pix manual, link de outra plataforma). Ela entra no motor pela mesma regra do ADR-001: a comissão, se houver, é do último toque da régua; quem fechou fica como “fechado por”."
      valorSugeridoCents={valorSugeridoCents}
      feeBps={feeBps}
      toque={toque}
      agora={agora}
      vendedoras={vendedoras}
      // Quem fechou e escolha da pessoa: pre-selecionar a primeira da equipe daria a venda a quem nao atendeu.
      vendedoraInicialId={null}
      permitirOutraPessoa
      politicaDeEstorno={politicaDeEstorno}
      onRegistrada={traduzir}
    />
  );
}
